import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {announce} from '@github-ui/aria-live'
import {ModelPicker} from '@github-ui/one-copilot-composer/ModelPicker'
import {ReasoningEffortPicker} from '@github-ui/one-copilot-composer/ReasoningEffortPicker'
import {ImageAttachButton as TaskChatInputImageAttachButton} from '@github-ui/one-copilot-composer/task-chat-input-toolbar/ImageAttachButton'
import {useLatestAgentModel} from '@github-ui/one-copilot-composer/use-latest-agent-model'
import {useLatestReasoningEffort} from '@github-ui/one-copilot-composer/use-latest-reasoning-effort'
import {useEntitlementQuotaNotice} from '@github-ui/copilot-shared-ui/hooks/use-entitlement-quota-notice'
import {useMarkdownImageHandling} from '@github-ui/one-copilot-composer/use-markdown-image-handling'
import {
  useModelSupportedReasoningEfforts,
  useReasoningEffortAutoFallback,
  useSelectModelClearingReasoningEffort,
} from '@github-ui/one-copilot-composer/use-model-supported-reasoning-efforts'
import {MeterButton} from '@github-ui/copilot-chat/components/MeterButton'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {FormControl, Link, useMergedRefs} from '@primer/react'
import {useCallback, useEffect, useRef, useState, type Ref} from 'react'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import {useTopLevelAgents} from '@github-ui/copilot-shared-ui/agents/use-top-level-agents'
import {useCreateAPISession} from '../../hooks/use-create-api-session'
import {type Session as SessionType, isPendingSession, isActiveSession} from '../../types/session'
import styles from './TaskChatInputV2.module.css'
import type {AgentTaskAppPayload} from '../../types/agent-task-app-payload'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {useCreateCommand} from '../../hooks/useCreateCommand'
import {TaskChatInputHeader} from './TaskChatInputHeader'
import {clsx} from 'clsx'
import {baseRefMatchesHeadRef, getBranchArtifactData} from '../../utils/task-artifact-helpers'
import type {APITask} from '../../types/api-task'
import {BlockedTaskInput} from './BlockedTaskInput'
import {ChatInputDisplay} from './ChatInputDisplay'
import {useViewMode} from '../../contexts/ViewModeContext'
import {useUnarchiveTask} from '../../hooks/use-unarchive-task'
import {isCopilotCodingAgent, isCopilotOrCustomAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {TaskStopButton} from './TaskStopButton'
import {QuotaBanner} from './QuotaBanner'
import {PromptInput} from './PromptInput'
import {usePromptResponseSubmission} from '../../hooks/use-prompt-response-submission'
import {resolveComposerPlaceholder, shouldShowStopButton} from './task-chat-input-presentation'
import {INCREASE_INDIVIDUAL_BUDGET_URL} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {useChronicleAutocomplete} from '@github-ui/copilot-shared-ui/hooks/use-chronicle-autocomplete'
import {ChronicleAutocomplete} from '../chronicle-autocomplete/ChronicleAutocomplete'
import {useFreshQuotaSnapshot} from '../../hooks/use-fresh-quota-snapshot'

const INDIVIDUAL_EXHAUSTED_ANALYTICS_CONTEXT = {
  category: 'general_ipm_ubb_user_exhausted_alerts_individual',
  scope: 'personal',
} as const
const INDIVIDUAL_BUDGET_SURFACE = 'agent_sessions_message_input'

export interface TaskChatInputProps {
  /** Allows drafting while preventing submission until instructions are ready. */
  draftOnly?: boolean
  /** This input is the interactive Chat product running against a managed sandbox. */
  interactiveSandboxChat?: boolean
  taskAgentId?: number
  activeSession: SessionType | null
  onAddPendingSession: (text: string, options?: {agentId?: number; isPRComment?: boolean}) => void
  onClearPendingSession: () => void
  payload: AgentTaskAppPayload
  scrollToBottom: (options?: {offset?: number}) => void
  repoNwo: string
  task: APITask
  branchAliveChannel?: string
  onViewDiff?: (filePath?: string) => void
  onCloseDiff?: () => void
  textAreaRef?: Ref<HTMLTextAreaElement>
}

export function TaskChatInputV2({
  draftOnly = false,
  taskAgentId,
  activeSession,
  onAddPendingSession,
  onClearPendingSession,
  payload,
  scrollToBottom,
  repoNwo,
  task,
  branchAliveChannel,
  onViewDiff,
  onCloseDiff,
  textAreaRef,
}: TaskChatInputProps) {
  const {pull, steeringEnabled, currentUserCanPush, agentQuotaSnapshot: payloadAgentQuotaSnapshot} = payload
  const tokenBasedBillingEnabled = payload.useTokenBasedBilling
  const agentQuotaSnapshot = useFreshQuotaSnapshot(payloadAgentQuotaSnapshot, activeSession)
  const isBlockedByQuota = agentQuotaSnapshot?.can_start_session === false
  const {notice: quotaNotice} = useEntitlementQuotaNotice({kind: 'agent', snapshot: agentQuotaSnapshot})
  const useIndividualBudgetCta = quotaNotice?.primaryAction?.href === INCREASE_INDIVIDUAL_BUDGET_URL
  const individualBudgetCtaShownRef = useRef(false)
  const trackIndividualBudgetCtaShown = useCallback((link: HTMLAnchorElement | null) => {
    if (!link || individualBudgetCtaShownRef.current) return

    individualBudgetCtaShownRef.current = true
    sendEvent('dotcom_chat.activate', {
      target: 'QUOTA_BANNER_EXCEEDED',
      action: 'shown',
      category: INDIVIDUAL_EXHAUSTED_ANALYTICS_CONTEXT.category,
      scope: INDIVIDUAL_EXHAUSTED_ANALYTICS_CONTEXT.scope,
      surface: INDIVIDUAL_BUDGET_SURFACE,
    })
  }, [])
  const viewMode = useViewMode()
  const tooltipDirection = viewMode === 'sidePanel' ? 'n' : undefined
  const [failedToSubmit, setFailedToSubmit] = useState(false)
  const {data: agentInfo, isLoading: isAgentInfoLoading} = useAgentInfo(taskAgentId)
  const {data: topLevelAgents, isLoading: isLoadingTopLevelAgents} = useTopLevelAgents(repoNwo)
  const agentNotEnabled =
    taskAgentId &&
    !isLoadingTopLevelAgents &&
    topLevelAgents &&
    topLevelAgents.length > 0 &&
    !topLevelAgents.some(agent => Number(agent.id) === taskAgentId)
  const name = agentInfo?.display_name?.toLowerCase() || 'agent'
  const is3PCodingAgent = !isAgentInfoLoading && ['claude', 'codex'].includes(name)
  const steerable = !!activeSession && !is3PCodingAgent
  const startingSession = activeSession && isPendingSession(activeSession)
  const taskCreatorLogin = task.creator?.login ?? null
  const {currentUserIsSessionOwner, currentUser} = useCurrentUser()
  const [text, setText] = useState('')
  const inputAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const mergedInputAreaRef = useMergedRefs(inputAreaRef, textAreaRef ?? null)
  const allChronSuggestions = useChronicleAutocomplete(text)
  const [chronicleDismissed, setChronicleDismissed] = useState(false)
  const chronicleSuggestions =
    (!taskAgentId || isCopilotCodingAgent(agentInfo?.slug)) && !chronicleDismissed ? allChronSuggestions : []
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canAutoUnarchive = !!task.archived_at && pull?.state !== 'merged'
  const {mutateAsync: unarchiveTask} = useUnarchiveTask({taskId: task.id, repoNwo})

  // Model selection
  const [selectedModelId, setSelectedModelId] = useLatestAgentModel()
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useLatestReasoningEffort()
  const supportedReasoningEfforts = useModelSupportedReasoningEfforts(selectedModelId)
  const selectModel = useSelectModelClearingReasoningEffort(
    setSelectedModelId,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
  )
  useReasoningEffortAutoFallback(selectedReasoningEffort, supportedReasoningEfforts, setSelectedReasoningEffort)
  const shouldShowModelPicker = isCopilotOrCustomAgent({topLevelAgentId: taskAgentId?.toString()}, agentInfo?.slug)
  const showMeterButton = tokenBasedBillingEnabled
  const showModelPicker = shouldShowModelPicker && !steerable

  // Image upload handling
  const repositoryId = task.repository?.id?.toString()
  const {
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isUploading: isImageUploading,
  } = useMarkdownImageHandling({
    textAreaRef: inputAreaRef,
    repositoryId,
  })

  const branchArtifact = getBranchArtifactData(task?.artifacts)
  const baseRef = (!baseRefMatchesHeadRef(branchArtifact) && branchArtifact?.base_ref) || pull?.base_ref_name
  const headRef = branchArtifact?.head_ref || pull?.head_ref_name
  const hasTaskChatInputHeader = !!baseRef && !!headRef && viewMode !== 'sidePanel'

  // Reset error state when user starts typing, with a small delay to prevent swallowing the first character
  useEffect(() => {
    const inputElement = inputAreaRef.current
    if (!inputElement) return

    let timeout: NodeJS.Timeout
    const handleInput = () => {
      if (failedToSubmit) {
        // Use a small timeout to ensure the character is processed first
        timeout = setTimeout(() => {
          setFailedToSubmit(false)
        }, 0)
      }
    }

    inputElement.addEventListener('input', handleInput)
    return () => {
      inputElement.removeEventListener('input', handleInput)
      if (timeout) clearTimeout(timeout)
    }
  }, [failedToSubmit, inputAreaRef])

  const {createSession} = useCreateAPISession({
    onError: () => {
      setFailedToSubmit(true)
      onClearPendingSession()
    },
  })

  const {
    pendingPrompt,
    pendingPromptIndex,
    pendingPromptCount,
    isExpired: isPendingPromptExpired,
    isPending: isCommandPending,
    goToPreviousPrompt,
    goToNextPrompt,
    handlePromptRespond,
  } = usePromptResponseSubmission({
    sessionId: activeSession?.id,
    taskId: task.id,
    enabled: !!activeSession && isActiveSession(activeSession),
    sessionState: activeSession?.state,
    onError: () => setFailedToSubmit(true),
  })

  const {createCommand} = useCreateCommand({
    onError: () => setFailedToSubmit(true),
  })

  const stopButton =
    activeSession && shouldShowStopButton({hasActiveSession: true, isAhpSession: false, isAhpSessionWorking: false}) ? (
      <TaskStopButton
        key={activeSession.id}
        session={activeSession}
        nwo={repoNwo}
        currentUserCanPush={currentUserCanPush}
        tooltipDirection={tooltipDirection}
      />
    ) : null

  const getBlockedText = (): string | React.ReactNode | undefined => {
    if (agentNotEnabled) {
      return 'Your organization has removed access for this agent. Contact your administrator for more information.'
    }

    // 3P agent is running, can't steer
    if (activeSession && is3PCodingAgent) {
      return `Wait until the session is complete before steering ${agentInfo?.display_name || 'agent'} sessions`
    }

    // User doesn't have access to steering features
    if (!steeringEnabled) {
      return 'Steering Copilot requires an active subscription'
    }

    if (task.archived_at && !canAutoUnarchive) {
      return 'This session has been archived'
    }

    if (!currentUserCanPush) {
      return 'You do not have write permission on the repository'
    }

    // Pull is closed/merged, no steering allowed
    if (pull?.state === 'merged') {
      return (
        <>
          This pull request was merged. To continue iterating,{' '}
          <Link href={`/${repoNwo}/agents`} inline>
            start a new session
          </Link>
          .
        </>
      )
    } else if (pull?.state === 'closed') {
      return 'This pull request was closed. To continue iterating, reopen the pull request.'
    }

    // Single session view — also check payload data directly to avoid a flash
    // during render transitions where the context value may lag behind the actual data.
    const isPayloadOwner = payload.sessions?.some(s => s.user_id === currentUser?.id)
    if (!currentUserIsSessionOwner && currentUser && !isPayloadOwner) {
      if (task.creator_type === 'organization') {
        return 'Sessions created by organizations cannot be steered.'
      }

      if (steerable) {
        return `Only ${taskCreatorLogin ?? 'the session owner'} can steer this session`
      }

      return 'Only the author of the task can ask for changes.'
    }

    if (isBlockedByQuota) {
      const description = (quotaNotice?.description ?? "You don't have enough AI credits to start a session.").replace(
        'start a session',
        'start or steer a session',
      )
      const {primaryAction, secondaryAction} = quotaNotice ?? {}
      if (!primaryAction && !secondaryAction) return description
      return (
        <>
          {description}{' '}
          {primaryAction && (
            <Link
              ref={useIndividualBudgetCta ? trackIndividualBudgetCtaShown : undefined}
              href={primaryAction.href}
              inline
              onClick={
                useIndividualBudgetCta
                  ? () =>
                      sendEvent('dotcom_chat.activate', {
                        target: 'QUOTA_BANNER_EXCEEDED',
                        action: 'increase_budget',
                        category: INDIVIDUAL_EXHAUSTED_ANALYTICS_CONTEXT.category,
                        scope: INDIVIDUAL_EXHAUSTED_ANALYTICS_CONTEXT.scope,
                        surface: INDIVIDUAL_BUDGET_SURFACE,
                      })
                  : undefined
              }
            >
              {primaryAction.label}
            </Link>
          )}
          {secondaryAction && (
            <>
              {primaryAction ? ' or ' : ''}
              <Link href={secondaryAction.href} inline>
                {primaryAction ? 'upgrade your plan' : 'Upgrade your plan'}
              </Link>
            </>
          )}
          {'.'}
        </>
      )
    }

    return undefined
  }

  // Consider the input blocked if there's any blocked text to show
  const blockedText = getBlockedText()
  const isBlocked = !!blockedText

  useEffect(() => {
    if (failedToSubmit && !isBlocked) {
      announce('Failed to submit latest message')
    }
  }, [failedToSubmit, isBlocked])

  // Not showing the quota banner when input is blocked
  const shouldShowQuotaBanner = !isBlocked

  // Get the container height dynamically for scroll offset (includes header + input)
  const getContainerOffset = () => containerRef.current?.offsetHeight ?? 0

  const handleActiveSessionCommandSubmit = (message: string) => {
    if (!steerable || !activeSession) return

    createCommand({
      sessionId: activeSession.id,
      content: message,
    })

    setText('')
  }

  const handleSubmit = async () => {
    // Always reset the error state when attempting a new submission
    setFailedToSubmit(false)

    try {
      // Prevent submitting empty messages or when blocked/starting/uploading
      if (!text.trim() || isBlocked || draftOnly || startingSession || isImageUploading) {
        return
      }

      if (steerable) {
        handleActiveSessionCommandSubmit(text)
        scrollToBottom()
        return
      }

      // Show the pending session immediately
      onAddPendingSession(text, {agentId: taskAgentId, isPRComment: false})

      // Unarchive must complete before creating a session
      if (canAutoUnarchive) {
        await unarchiveTask()
      }

      // Clear the input
      setText('')
      onCloseDiff?.()

      createSession({
        nwo: repoNwo,
        taskId: task.id,
        problemStatement: text,
        ...(shouldShowModelPicker && selectedModelId ? {model: selectedModelId} : {}),
        ...(shouldShowModelPicker && selectedReasoningEffort ? {reasoningEffort: selectedReasoningEffort} : {}),
        agentId: taskAgentId,
        eventType: 'agent_task_post_session_steering',
      })

      requestAnimationFrame(() => {
        scrollToBottom({offset: getContainerOffset()})
      })
    } catch {
      setFailedToSubmit(true)

      // Throwing this error will repopulate the text in the input
      throw new ChatInputValidationError(text)
    }
  }

  const placeHolderText = resolveComposerPlaceholder({
    startingSession: !!startingSession,
    isAhpSession: false,
    steerable,
  })

  return (
    <div
      className={clsx(styles.container, isDraggedOver && styles.isDraggedOver)}
      ref={containerRef}
      {...dropTargetProps}
    >
      {shouldShowQuotaBanner && (
        <div className={clsx(hasTaskChatInputHeader && styles.quotaBannerWithHeader)}>
          <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
        </div>
      )}
      {hasTaskChatInputHeader && (
        <TaskChatInputHeader
          pull={pull}
          repoNwo={repoNwo}
          taskId={task.id}
          baseRef={baseRef}
          headRef={headRef}
          branchAliveChannel={branchAliveChannel}
          // `waiting_for_user` is technically `isActiveSession()`, but for the
          // chat-input header (Changes button, Create-pull-request button) it
          // should be treated as paused — the runtime container has exited
          // and there's no in-flight work that would invalidate the diff or
          // racing PR creation. Without this, after a resume completes and
          // the old frozen `waiting_for_user` session becomes `activeSession`
          // again, the Create-pull-request button stays disabled.
          isSessionInProgress={
            activeSession ? isActiveSession(activeSession) && activeSession.state !== 'waiting_for_user' : false
          }
          onViewDiff={onViewDiff}
        />
      )}
      {pendingPrompt && !isBlocked ? (
        isPendingPromptExpired ? (
          <div className={clsx(styles.inputWrapper, styles.inputWrapperWithToolbar, styles.withImageUpload)}>
            <BlockedTaskInput>This pending prompt has expired. Start a new task to continue.</BlockedTaskInput>
          </div>
        ) : (
          <>
            <PromptInput
              prompt={pendingPrompt}
              promptNavigation={{
                current: pendingPromptIndex + 1,
                total: pendingPromptCount,
                onPrevious: goToPreviousPrompt,
                onNext: goToNextPrompt,
              }}
              onRespond={handlePromptRespond}
              disabled={isCommandPending}
            />
            {failedToSubmit && <div className="pt-2 color-fg-danger text-small">Failed to submit latest message</div>}
          </>
        )
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault()
            handleSubmit()
          }}
          id="task-chat-input-form"
          data-testid="task-chat-input-form"
        >
          <FormControl>
            <FormControl.Label visuallyHidden>Send message</FormControl.Label>
            {isBlocked ? (
              <div className={clsx(styles.inputWrapper, styles.inputWrapperWithToolbar, styles.withImageUpload)}>
                <BlockedTaskInput>{blockedText}</BlockedTaskInput>
              </div>
            ) : (
              <ChatInputDisplay
                ref={mergedInputAreaRef}
                value={text}
                onChange={setText}
                onSubmit={handleSubmit}
                placeholder={placeHolderText}
                disabled={isBlocked}
                isSubmitting={Boolean(draftOnly || startingSession || isImageUploading)}
                sendButtonAriaLabel="Send now"
                allowEmptySubmit
                onPaste={onPaste}
                stopSlot={stopButton}
                tooltipDirection={tooltipDirection}
                afterTextarea={
                  <ChronicleAutocomplete
                    suggestions={chronicleSuggestions}
                    onSelect={setText}
                    onDismiss={() => setChronicleDismissed(true)}
                    textAreaRef={inputAreaRef}
                    currentText={text}
                  />
                }
                toolbarSlot={
                  <>
                    <TaskChatInputImageAttachButton
                      textAreaRef={inputAreaRef}
                      repositoryId={repositoryId}
                      tooltipDirection={tooltipDirection}
                    />
                    {showModelPicker ? (
                      <>
                        <ModelPicker
                          selectedModelId={selectedModelId}
                          onUpdateModel={selectModel}
                          isTokenBasedBillingEnabled={tokenBasedBillingEnabled}
                        />
                        {copilotFeatureFlags.codingAgentReasoningEffort && (
                          <ReasoningEffortPicker
                            selectedReasoningEffort={selectedReasoningEffort}
                            onUpdateReasoningEffort={setSelectedReasoningEffort}
                            supportedReasoningEfforts={supportedReasoningEfforts}
                          />
                        )}
                      </>
                    ) : null}
                    {showMeterButton && <MeterButton mode="mission_control" />}
                  </>
                }
              />
            )}
            {failedToSubmit && !isBlocked && (
              <FormControl.Validation variant="error" className="pt-2">
                Failed to submit latest message
              </FormControl.Validation>
            )}
          </FormControl>
        </form>
      )}
    </div>
  )
}
